import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from '@trashlab/viteapp/src/lib/utils';

const badgeVariants = cva(
  'inline-flex items-center rounded-md border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
  {
    variants: {
      variant: {
        default:
          'border-transparent bg-primary text-primary-foreground shadow hover:bg-primary/80',
        light: 'text-primary bg-primary/10 hover:bg-primary/40',
        secondary:
          'border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80',
        destructive:
          'border-transparent bg-destructive text-destructive-foreground shadow hover:bg-destructive/80',
        outline: 'text-foreground',
        success:
          'border-transparent bg-green-500 text-white shadow hover:bg-green-600',
        // Additive tones (the way ui/alert.tsx added warning/success):
        // amber-500 is already the app's warning chip ink (see the quotes-v2
        // CHANGES_REQUESTED badge); info is the soft blue disclosure chip.
        warning:
          'border-transparent bg-amber-500 text-white shadow hover:bg-amber-500/80',
        info: 'border-transparent bg-blue-500/15 text-blue-700 hover:bg-blue-500/20',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {}

const Badge = React.forwardRef<HTMLDivElement, BadgeProps>(
  ({ className, variant, ...props }, ref) => {
    return (
      <div
        data-slot="badge"
        data-variant={variant ?? 'default'}
        className={cn(badgeVariants({ variant }), className)}
        ref={ref}
        {...props}
      />
    )
  }
)
Badge.displayName = "Badge"

export { Badge, badgeVariants }
